import React, {FC, useEffect, useRef, useState} from "react";
import moment from "moment";
import {Button, Checkbox, Modal, Table, Tag, Typography, message} from "antd";
import {GetBookingFeeLettersQuery, useCreateFeeLetterMutation, useGetBookingFeeLettersQuery, useGetFeeLetterPdfLazyQuery} from "../../../../generated/graphql";
import i18n from "../../../../services/i18n";
import PanelCard from "../../../../views/PanelCard/PanelCard";
import {renderAmount} from "../../../../helpers/renderAmount";
import {downloadPdfFromBase64} from "../../../../helpers/downloadPdfFromBase64";
import {resolveServerErrorMessage} from "../../../../helpers/resolveServerErrorMessage";
import {useHasRoleAtLeast} from "../../../../helpers/useIsStaffOnlyAccess";
import Roles from "../../../../../shared/models/roles";

type FeeLetter = GetBookingFeeLettersQuery["feeLettersByBooking"][number];
type Proposal = GetBookingFeeLettersQuery["feeLetterProposal"];

// Getter statt Modul-Konstante: ein i18n-Proxy als Modul-Binding bringt react-hot-loader im Dev-Modus zum Absturz
const tr = () => i18n.containers.invoice.BookingFeeLetters;
const date = (value?: string | Date | null) => (value ? moment(value).format("DD.MM.YYYY") : "–");
const money = (amount: number) => renderAmount({amount});

type Payment = {id: string; paymentAmount?: number | null; openAmount?: number | null; paymentDone?: unknown; deletedAt?: unknown};

type Props = {
    bookingId: string;
    /** Ohne Rechnung gibt es keine Rechnungsnummer für das Schreiben */
    hasInvoice: boolean;
    /** Posten der Buchung aus der Karte: ändern sie sich (Gebühr angelegt, bezahlt, erlassen), lädt der Abschnitt neu */
    scheduledPayments?: ReadonlyArray<Payment> | null;
};

/** Fingerabdruck der Posten, damit eine neu geladene Buchung den Vorschlag auffrischt */
const paymentsKey = (payments?: ReadonlyArray<Payment> | null) =>
    (payments ?? []).map((p) => `${p.id}:${p.paymentAmount ?? ""}:${p.openAmount ?? ""}:${p.paymentDone ? 1 : 0}:${p.deletedAt ? 1 : 0}`).join("|");

/** Gebühren-Schreiben (#1517, ADR 0021): für alle Tenants ab Rolle Admin, sichtbar bei offenen Gebühren oder vorhandenen Schreiben */
const BookingFeeLettersPanel: FC<Props> = ({bookingId, hasInvoice, scheduledPayments}) => {
    const isAdmin = useHasRoleAtLeast(Roles.Admin);

    if (!isAdmin || !hasInvoice) {
        return null;
    }

    return <FeeLettersCard bookingId={bookingId} postsKey={paymentsKey(scheduledPayments)} />;
};

const FeeLettersCard: FC<{bookingId: string; postsKey: string}> = ({bookingId, postsKey}) => {
    const {data, refetch} = useGetBookingFeeLettersQuery({variables: {bookingId}, fetchPolicy: "network-only"});
    const firstKey = useRef(postsKey);

    // Erster Stand kommt aus der Query selbst; danach bei jeder Änderung der Posten neu laden (Codex #1709)
    useEffect(() => {
        if (postsKey !== firstKey.current) {
            firstKey.current = postsKey;
            void refetch();
        }
    }, [postsKey, refetch]);
    const [loadPdf] = useGetFeeLetterPdfLazyQuery({fetchPolicy: "network-only"});
    const [createOpen, setCreateOpen] = useState(false);
    const letters = data?.feeLettersByBooking ?? [];
    const proposal = data?.feeLetterProposal;
    const canCreate = Boolean(proposal && !proposal.blocker && proposal.items.length > 0);

    if (!canCreate && letters.length === 0) {
        return null;
    }

    const downloadPdf = async (letter: {id: string; letterNumber: string}) => {
        try {
            const result = await loadPdf({variables: {id: letter.id}});

            if (result.error) {
                throw result.error;
            }
            downloadPdfFromBase64(result.data?.feeLetterPdf ?? "", `${letter.letterNumber}.pdf`);
        } catch (error) {
            message.error(resolveServerErrorMessage(error, tr().failed()));
        }
    };

    const columns = [
        {title: tr().columns.number(), dataIndex: "letterNumber", key: "number"},
        {title: tr().columns.date(), key: "date", render: (_: unknown, l: FeeLetter) => date(l.createDate)},
        {title: tr().columns.total(), key: "total", align: "right" as const, render: (_: unknown, l: FeeLetter) => money(l.totalAmount)},
        {title: tr().columns.due(), key: "due", render: (_: unknown, l: FeeLetter) => date(l.paymentDue)},
        {
            title: tr().columns.sent(),
            key: "sent",
            render: (_: unknown, l: FeeLetter) => (l.sentAt ? <Tag color="green">{tr().sent({date: moment(l.sentAt).format("DD.MM. HH:mm")})}</Tag> : <Tag>{tr().notSent()}</Tag>),
        },
        {
            title: "",
            key: "actions",
            render: (_: unknown, l: FeeLetter) => (
                <Button type="link" size="small" onClick={async () => downloadPdf(l)}>
                    {tr().pdf()}
                </Button>
            ),
        },
    ];

    return (
        <PanelCard
            title={tr().title()}
            columns={1}
            gapAfter
            extra={
                canCreate && (
                    <Button
                        type="primary"
                        onClick={async () => {
                            // Gebühren können sich in anderen Abschnitten der Karte geändert haben (Mahnung, Zahlung)
                            await refetch();
                            setCreateOpen(true);
                        }}
                    >
                        {tr().create()}
                    </Button>
                )
            }
        >
            {canCreate && (
                <Typography.Paragraph type="secondary" style={{marginBottom: 12}}>
                    {tr().intro()}
                </Typography.Paragraph>
            )}
            {letters.length > 0 && <Table rowKey="id" size="small" pagination={false} dataSource={letters} columns={columns} />}
            {createOpen && proposal && <CreateFeeLetterModal bookingId={bookingId} proposal={proposal} onClose={() => setCreateOpen(false)} onDone={refetch} onDownload={downloadPdf} />}
        </PanelCard>
    );
};

const CreateFeeLetterModal: FC<{
    bookingId: string;
    proposal: Proposal;
    onClose: () => void;
    onDone: () => Promise<unknown>;
    onDownload: (letter: {id: string; letterNumber: string}) => Promise<void>;
}> = ({bookingId, proposal, onClose, onDone, onDownload}) => {
    const email = proposal.recipientEmail;
    const [sendMail, setSendMail] = useState(Boolean(email));
    const [create, {loading}] = useCreateFeeLetterMutation();

    const submit = async () => {
        try {
            const result = await create({variables: {input: {bookingId, sendMail: sendMail && Boolean(email), letterNumber: proposal.letterNumber ?? "", totalAmount: proposal.totalAmount}}});
            const created = result.data?.createFeeLetter;

            onClose();
            await onDone();
            if (!created) {
                return;
            }
            if (created.sentAt) {
                message.success(tr().dialog.createdSent({number: created.letterNumber}));
            } else {
                // Mail gewünscht, aber nicht angenommen: das Schreiben steht trotzdem, das PDF kommt zum Selbstversand
                if (sendMail && email) {
                    message.warning(tr().dialog.mailFailed({number: created.letterNumber}));
                } else {
                    message.success(tr().dialog.created({number: created.letterNumber}));
                }
                await onDownload(created);
            }
        } catch (error) {
            message.error(resolveServerErrorMessage(error, tr().failed()));
            await onDone();
        }
    };

    return (
        <Modal
            visible
            title={tr().dialog.title({number: proposal.letterNumber ?? ""})}
            onCancel={onClose}
            onOk={submit}
            okText={tr().dialog.submit()}
            cancelText={tr().dialog.cancel()}
            confirmLoading={loading}
        >
            <Typography.Paragraph>{tr().dialog.items()}</Typography.Paragraph>
            <Table
                rowKey="scheduledPaymentId"
                size="small"
                pagination={false}
                dataSource={proposal.items}
                columns={[
                    {title: "", dataIndex: "title", key: "title"},
                    {title: "", key: "amount", align: "right" as const, render: (_: unknown, item: Proposal["items"][number]) => money(item.amount)},
                ]}
                showHeader={false}
                summary={() => (
                    <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                            <strong>{tr().dialog.total()}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                            <strong>{money(proposal.totalAmount)}</strong>
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                )}
            />
            <Typography.Paragraph type="secondary" style={{marginTop: 12}}>
                {tr().dialog.dueHint()}
            </Typography.Paragraph>
            {email ? (
                <Checkbox checked={sendMail} onChange={(e) => setSendMail(e.target.checked)}>
                    {tr().dialog.sendMail({email})}
                </Checkbox>
            ) : (
                <Typography.Paragraph type="warning">{tr().dialog.noEmail()}</Typography.Paragraph>
            )}
        </Modal>
    );
};

export default BookingFeeLettersPanel;
